import Button from '../shared/Button.js';
// SheepSendToProcessorModal — mirror of CattleSendToProcessorModal.
// Pops up when a sheep weigh-in session is being completed and at least
// one entry has send_to_processor=true. Gate is intentionally looser than
// cattle's finishers-only — any draft session, any flock per §7. User
// picks an existing unfinished processing batch OR creates a new one; on
// confirm, the flagged entries' sheep are attached to the batch
// (live_weight from this session's entries), moved to the 'processed'
// flock, and a sheep_transfers row logged per sheep.
//
// The batch is promoted to 'active' (displayed "In Process") as part of the
// same send — migration 206. Its dates are NOT touched: actual_process_date
// still means "processed" for sheep, and it is set when the batch is marked
// complete from the Batches tab once the processor is done.
import React from 'react';
import type {CSSProperties} from 'react';
import {loadPlannedSheepProcessingBatches, nextSheepBatchName} from '../lib/sheepProcessingBatch.js';
import type {PlannedSheepProcessingBatchRow} from '../lib/sheepProcessingBatch.js';
import {attachSheepToProcessingBatch} from '../lib/processingAttachApi.js';
import {ensureProcessingFreshness} from '../lib/processingApi.js';

import {useAuth} from '../contexts/AuthContext.jsx';

// One unfinished sheep_processing_batches row, as the picker reads it. The
// shape belongs to sheepProcessingBatch.ts, the module that issues the read.
type PlannedBatch = PlannedSheepProcessingBatchRow;

// A flagged weigh-in entry. `id` is the weigh_ins primary key and is therefore
// required — that is what lets `.map(e => e.id).filter(Boolean)` below stay
// exactly as written and still produce string[] for the attach RPC. `weight`
// stays wide because it is a numeric column when it arrives from the database
// and an input string while the row is being edited.
type FlaggedEntry = {
  id: string;
  weight?: number | string | null;
  // Read only by the confirmation summary, which lists which sheep are about to
  // leave the flock. Optional because an entry can be mid-edit and tagless —
  // the attach path skips those with reason 'tagless'.
  tag?: string | null;
};

// One entry the send did NOT move, as the RPC reports it.
type SendSkip = {
  entry_id?: string | null;
  tag?: string | null;
  reason?: string | null;
  batch_id?: string | null;
  batch_name?: string | null;
};

// What actually happened, held for acknowledgement when it is not simply
// "everything you flagged went where you asked".
type SendOutcome = {
  attached: number;
  skipped: SendSkip[];
  filledDate: string | null;
  batch: unknown;
};

const SKIP_REASONS: Record<string, string> = {
  tagless: 'no tag on the entry',
  no_sheep_for_tag: 'no sheep matches that tag',
  already_in_batch: 'already on this batch',
  attached_to_other_batch: 'already sent to another batch',
};

type SheepSendToProcessorModalProps = {
  // The Supabase client, handed down as a prop rather than imported here.
  // Typed through src/lib/supabase.ts's global `Window { sb }` declare so this
  // module does not gain a supabase import edge it has never had.
  sb: Window['sb'];
  // Required, despite the `session && ...` guards the body keeps: BOTH call
  // sites prove a session exists before rendering this modal. WeighInSessionPage
  // gates on `sessionForModal &&`, and WeighInsWebform's completeSession() —
  // the only place that sets showProcessorModal — starts with
  // `if (!session) return;`. The guards below are left untouched.
  session: {id: string; date?: string | null};
  flaggedEntries: FlaggedEntry[];
  teamMember?: string | null;
  // A batch chosen before the weigh-in started, carried here through route
  // state by "Send sheep to processor" on Sheep Batches or a batch record.
  // Advisory: it preselects the picker, it does not lock it.
  preselectBatchId?: string | null;
  onCancel: () => void;
  // Both call sites pass an async handler that ignores the argument; the modal
  // never awaits the result.
  onConfirmed: (result: {batch: unknown; attached: unknown; skipped: unknown}) => void;
};

export default function SheepSendToProcessorModal({
  sb,
  session,
  flaggedEntries,
  teamMember,
  preselectBatchId = null,
  onCancel,
  onConfirmed,
}: SheepSendToProcessorModalProps) {
  // The role is read HERE rather than taken as a prop, and that placement is
  // the point. This modal lives in src/sheep, an authenticated domain folder,
  // so it may use AuthContext. Its other entry point is the /weighins presenter
  // in src/webforms, which may NOT: that module is held to an identity-only
  // contract (name and nothing else) by WeighInsAuthenticatedBridge, precisely
  // so a webform module can never inspect or choose an authenticated session.
  // Threading the role through it would have widened that boundary for a value
  // the modal can read for itself.
  //
  // It decides only what this modal OFFERS. attach_sheep_to_processing_batch is
  // SECURITY DEFINER and re-derives the caller's role from auth.uid() before it
  // writes, so this is an affordance, never the permission.
  const {authState} = useAuth() as {authState: {role?: string | null} | null | false};
  const role = authState ? authState.role : null;
  const canSend = role === 'admin' || role === 'management';
  // Explicit argument: useState([]) alone infers never[], and every read below
  // (b.id, b.name, b.planned_process_date, b.sheep_detail) would be an error on
  // an element type that can hold nothing.
  const [plannedBatches, setPlannedBatches] = React.useState<PlannedBatch[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [mode, setMode] = React.useState('existing'); // 'existing' | 'new'
  const [batchId, setBatchId] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [newPlannedDate, setNewPlannedDate] = React.useState(
    (session && session.date) || new Date().toISOString().slice(0, 10),
  );
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  // Why the suggested name is missing, when it is. It never blocks the send:
  // migration 206's RPC allocates its own name when the caller sends none.
  const [nameError, setNameError] = React.useState('');
  // Set only when the send did something the operator did not explicitly ask
  // for; while it is set the modal shows what happened instead of the picker.
  const [outcome, setOutcome] = React.useState<SendOutcome | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    // Still NOT awaited, and still guarded by the `cancelled` flag above: the
    // loader returns the request itself, so this continuation runs on exactly
    // the tick it always did. Only the chain moved.
    loadPlannedSheepProcessingBatches(sb).then(({data, error}) => {
      if (cancelled) return;
      if (error) {
        setErr('Could not load batches: ' + error.message);
        setLoading(false);
        return;
      }
      const rows = data || [];
      setPlannedBatches(rows);
      if (rows.length === 0) setMode('new');
      // A batch carried in from "Send sheep to processor" preselects itself, so
      // the operator confirms the destination they already chose instead of
      // finding it again. Only when it is actually in the picker: a batch
      // completed or already processed since that click is no longer a valid
      // destination, and silently selecting nothing would be worse than falling
      // back to the normal choice.
      if (preselectBatchId && rows.some((b) => b.id === preselectBatchId)) {
        setMode('existing');
        setBatchId(preselectBatchId);
      }
      // The next batch name is NOT derived from `rows`. These are unfinished
      // batches only, and numbering off them proposed names already taken by
      // completed batches — the UNIQUE constraint on name then rejected the
      // send. The allocation is the server's now; see the effect below.
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [sb, preselectBatchId]);

  // Batch-name allocation, separate from the picker read above so that read
  // keeps its exact timing contract. Migration 206's allocator scans every
  // status, which is the whole point of moving it off the client.
  React.useEffect(() => {
    let cancelled = false;
    nextSheepBatchName(sb)
      .then((name) => {
        if (cancelled) return;
        setNewName(name);
        setNameError('');
      })
      .catch((e) => {
        if (cancelled) return;
        setNewName('');
        setNameError(e && (e as Error).message ? (e as Error).message : 'Could not allocate a batch name.');
      });
    return () => {
      cancelled = true;
    };
  }, [sb]);

  // String() rather than a cast: parseFloat coerces its argument with ToString
  // anyway, so this is the same call for every value that reaches it (a numeric
  // column, an input string, null, or undefined all give the identical result).
  const totalWeight = flaggedEntries.reduce((s, e) => s + (parseFloat(String(e.weight)) || 0), 0);

  // The date a send records as the actual processing date when an EXISTING
  // batch is promoted. Same resolution the RPC performs server-side.
  const sessionProcessingDate = (session && session.date) || new Date().toISOString().slice(0, 10);

  // The batch this send lands in, resolved for the confirmation summary below.
  const selectedBatch = mode === 'existing' ? plannedBatches.find((b) => b.id === batchId) || null : null;

  // An existing destination that was never given a processor date. The send has
  // to record one -- migration 176's reconciler skips a dateless batch, so it
  // would take on sheep and never appear on the Processing calendar -- but the
  // date is CONFIRMED here rather than assumed. An empty booking field is not
  // evidence that processing happens on shipping day.
  const destinationNeedsDate = mode === 'existing' && !!selectedBatch && !selectedBatch.planned_process_date;
  // Does this send have to ESTABLISH a date? A new batch always does; an
  // existing destination does only when it has none booked.
  const dateRequired = mode === 'new' || destinationNeedsDate;
  // The date this send will record. NO FALLBACK: `newPlannedDate || suggestion`
  // meant clearing the field silently resubmitted the suggestion, leaving
  // Confirm enabled and writing a date the operator had just deleted. The
  // suggestion is the INITIAL value of the input (see useState above) and
  // nothing more; what the field holds now is what gets written.
  const enteredDate = (newPlannedDate || '').trim();
  const enteredDateValid = /^\d{4}-\d{2}-\d{2}$/.test(enteredDate);
  const effectiveDate = dateRequired ? enteredDate : selectedBatch?.planned_process_date || '';

  // One predicate, read three times by the confirm button (disabled, colour,
  // cursor) and matching the guards at the top of go(). A missing name is NOT
  // here: migration 206 allocates server-side when the caller sends none, so a
  // failed suggestion is a notice, not a blocker.
  const confirmDisabled =
    busy ||
    loading ||
    !canSend ||
    (mode === 'existing' && !batchId) ||
    // Whenever this send establishes a date, the field itself must hold one.
    (dateRequired && !enteredDateValid);

  async function go() {
    setErr('');
    if (mode === 'existing' && !batchId) {
      setErr('Pick a batch or switch to + New.');
      return;
    }
    if (!canSend) {
      setErr('Send-to-Processor is restricted to management/admin.');
      return;
    }
    // Repeated here and not left to the disabled button: the button is a
    // rendering decision and this is the last point before a write. The RPC
    // refuses a missing date too, so all three agree.
    if (dateRequired && !enteredDateValid) {
      setErr('Enter the processor date for this batch before sending.');
      return;
    }
    setBusy(true);
    try {
      // ONE send path. There used to be a second, client-side one for the
      // login-gated webform: it created or promoted the batch, then wrote the
      // roster, the animal, the transfer and the weigh-in linkage as separate
      // statements, swallowing errors on the later ones. An interruption
      // anywhere in that sequence left a half-sent batch, and the promotion this
      // lane added would have been one more step in it. Both entry points now
      // call the transactional RPC, which does the whole thing under one
      // transaction and its own row locks.
      //
      // `batch` is the jsonb envelope's, so it really is unknown; it is
      // forwarded to onConfirmed, whose callers ignore the argument.
      if (mode === 'existing') {
        const selected = plannedBatches.find((b) => b.id === batchId);
        if (!selected) throw new Error('Selected batch not found.');
      }
      const result = await attachSheepToProcessingBatch(sb, {
        sessionId: session && session.id,
        entryIds: flaggedEntries.map((e) => e.id).filter(Boolean),
        targetBatchId: mode === 'existing' ? batchId : null,
        batchName: mode === 'new' ? newName : null,
        // `|| sessionProcessingDate` mirrors the RPC's own
        // COALESCE(p_planned_date, session.date, CURRENT_DATE): a cleared date
        // input must not produce a batch with no date, because the
        // migration-176 reconciler skips those and they never reach the
        // Processing calendar.
        // Sent for a new batch AND for an undated destination; withheld when
        // the destination already has a booked date, which the RPC then leaves
        // exactly as booked.
        plannedDate: dateRequired ? enteredDate : null,
        teamMember,
      });
      const batch: unknown = result.batch;
      // The RPC envelope types these as unknown-ish; the shapes are the
      // attached/skipped arrays the function builds. Narrowed once, here, so
      // the outcome panel below can count and list them.
      const attached: unknown[] = Array.isArray(result.attached) ? result.attached : [];
      const skipped: SendSkip[] = Array.isArray(result.skipped) ? (result.skipped as SendSkip[]) : [];
      // Nudge the Processing reconciler so the calendar — and anything hanging
      // off the Processing record — sees this batch without waiting for the next
      // sweep. Best-effort in every sense: the RPC floors the age at 15s and
      // returns {ran:false} if a reconcile just happened, it no-ops under its
      // advisory lock when one is already running, and it refuses roles outside
      // farm_team/management/admin. None of that may undo a committed send.
      try {
        await ensureProcessingFreshness(sb, 15);
      } catch (_e) {
        /* the send is committed; freshness is an optimization */
      }
      // A SEND IS NOT ALWAYS ALL-OR-NOTHING. Sheep can be skipped — tagless,
      // no matching sheep, already on this batch, or already belonging to
      // another one — and both callers ignore this result and go straight on to
      // finalize the session. Closing silently would let a send that moved two
      // of five sheep look like it moved five, and the flock is where the
      // operator would have to notice.
      //
      // So anything the operator did not ask for holds the modal open until
      // they acknowledge it. A clean send closes exactly as before.
      const filledDate = (result as {filled_planned_date?: boolean}).filled_planned_date === true;
      // Gated on SKIPS only. A filled date is not a surprise: it is required in
      // this dialog, shown in the summary as "recorded by this send", and
      // confirmed by the operator before they pressed the button. Holding them
      // for a second acknowledgement of their own input is friction, not
      // safety. It is still reported below when a panel shows for a skip.
      if (skipped.length > 0) {
        setOutcome({
          attached: attached.length,
          skipped: skipped as SendSkip[],
          filledDate: filledDate ? effectiveDate : null,
          batch,
        });
        setBusy(false);
        return;
      }
      onConfirmed({batch, attached, skipped});
    } catch (e) {
      // `e` is `unknown` under strict mode, and the shapes that actually reach
      // here are Error and the plain PostgrestError objects supabase-js rejects
      // with — neither of which `instanceof Error` alone covers. Same narrowing
      // src/lib/entityMutations.ts already uses for its catch variables, and it
      // reproduces `e.message || fallback` for every value with a message.
      const message = e && typeof e === 'object' && 'message' in e ? (e as {message?: unknown}).message : undefined;
      setErr(message ? String(message) : 'Could not attach to batch.');
      setBusy(false);
    }
  }

  const lblS: CSSProperties = {display: 'block', fontSize: 12, color: 'var(--ink)', marginBottom: 4, fontWeight: 600};
  const inpS: CSSProperties = {
    fontFamily: 'inherit',
    fontSize: 13,
    padding: '8px 10px',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--surface)',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        zIndex: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-modal)',
          maxWidth: 480,
          width: '100%',
          padding: '18px 20px',
          boxShadow: 'var(--shadow-modal)',
          // The outcome list grows with the number of skipped sheep, and
          // Continue sits below it. On a phone an unconstrained card pushes that
          // button off-screen with nothing to scroll — the acknowledgement this
          // send now REQUIRES would be unreachable. 32px is the wrapper's 16px
          // padding on both edges.
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
        }}
      >
        <div style={{fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6}}>
          {'🚩 Send ' + flaggedEntries.length + ' sheep to processor'}
        </div>
        {!outcome && (
          <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10}}>
            {'Total live weight: ' +
              Math.round(totalWeight).toLocaleString() +
              ' lb. These sheep will move to the Processed flock and attach to the batch, and the batch moves to In Process. ' +
              (destinationNeedsDate
                ? 'This batch has no processor date yet, so the send will record the one below. '
                : 'Its processor date is unchanged. ') +
              'Mark it Complete from the Batches tab once the processor is done.'}
          </div>
        )}

        {/* Which sheep, and where they are going — shown before the confirm
            rather than only in the resulting batch. Tags are listed because a
            miscounted flag is the mistake this send cannot undo. */}
        {!outcome && (
          <div
            data-sheep-send-summary
            style={{
              marginBottom: 14,
              padding: '8px 10px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              fontSize: 11,
              color: 'var(--ink)',
            }}
          >
            <div style={{marginBottom: 4}}>
              <strong>{'Destination: '}</strong>
              {mode === 'existing'
                ? selectedBatch
                  ? (selectedBatch.name || selectedBatch.id) +
                    (selectedBatch.planned_process_date ? ' · booked ' + selectedBatch.planned_process_date : '')
                  : 'pick a batch below'
                : (newName || 'a new batch, named by the server') + ' · new'}
            </div>
            {/* The date this send will WRITE, spelled out. For an undated
              destination it is the one being recorded, not a restatement. */}
            <div style={{marginBottom: 4}}>
              <strong>{'Processor date: '}</strong>
              {effectiveDate || '—'}
              {destinationNeedsDate ? ' · recorded by this send' : ''}
            </div>
            <div style={{color: 'var(--ink-muted)', wordBreak: 'break-word'}}>
              <strong>{'Sheep (' + flaggedEntries.length + '): '}</strong>
              {flaggedEntries.map((e) => (e.tag ? String(e.tag) : '(no tag)')).join(', ')}
            </div>
          </div>
        )}

        {/* WHAT ACTUALLY HAPPENED. Shown instead of the picker once the send has
            committed and did not do exactly what was asked. The session is not
            finalized until this is acknowledged, so a partial send cannot slip
            past as a complete one. */}
        {outcome && (
          <div
            data-sheep-send-outcome
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              border: '1px solid var(--border-strong)',
              background: 'var(--surface-2)',
              fontSize: 12,
              color: 'var(--ink)',
              lineHeight: 1.5,
            }}
          >
            <div style={{fontWeight: 700, marginBottom: 6}}>
              {outcome.attached === 0
                ? 'No sheep were sent.'
                : 'Sent ' + outcome.attached + (outcome.attached === 1 ? ' sheep.' : ' sheep.')}
            </div>
            {outcome.filledDate && (
              <div style={{marginBottom: 6}}>
                Recorded <strong>{outcome.filledDate}</strong> as this batch&apos;s processor date.
              </div>
            )}
            {outcome.skipped.length > 0 && (
              <>
                <div style={{marginBottom: 4}}>
                  {outcome.skipped.length === 1
                    ? '1 sheep was not sent:'
                    : outcome.skipped.length + ' sheep were not sent:'}
                </div>
                <ul style={{margin: 0, paddingLeft: 18}}>
                  {outcome.skipped.map((s, i) => (
                    <li key={s.entry_id || i} style={{marginBottom: 2}}>
                      <strong>{s.tag || '(no tag)'}</strong>
                      {' — '}
                      {SKIP_REASONS[String(s.reason)] || String(s.reason || 'skipped')}
                      {s.batch_name ? ' (' + s.batch_name + ')' : ''}
                    </li>
                  ))}
                </ul>
                <div style={{marginTop: 6, color: 'var(--ink-muted)'}}>
                  These sheep stayed in their flock. Nothing else about this send is affected.
                </div>
              </>
            )}
          </div>
        )}

        {/* Someone who cannot send must not be left staring at a dead confirm
            button, and must not be pushed into completing the session either —
            completing it clears the draft the flags live on, and management
            would have nothing left to send. So the picker is replaced by the
            handoff, and the only action leaves everything exactly as it is. */}
        {!outcome && !canSend && (
          <div
            data-sheep-send-handoff
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              border: '1px solid var(--border-strong)',
              background: 'var(--surface-2)',
              fontSize: 12,
              color: 'var(--ink)',
              lineHeight: 1.5,
            }}
          >
            Sending sheep to the processor is done by management. Your weights and the {flaggedEntries.length} flagged{' '}
            {flaggedEntries.length === 1 ? 'sheep' : 'sheep'} are saved. Leave this session as a draft so management can
            complete the send from Weigh-In Sessions.
          </div>
        )}

        {!outcome && canSend && loading && (
          <div style={{padding: '20px 0', textAlign: 'center', color: 'var(--ink-faint)', fontSize: 12}}>
            Loading batches{'…'}
          </div>
        )}

        {!outcome && canSend && !loading && (
          <div style={{marginBottom: 10}}>
            <div style={{display: 'flex', gap: 6, marginBottom: 8}}>
              <button
                type="button"
                onClick={() => setMode('existing')}
                disabled={plannedBatches.length === 0}
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  borderRadius: 10,
                  border: '1px solid ' + (mode === 'existing' ? 'var(--brand)' : 'var(--border-strong)'),
                  background: mode === 'existing' ? 'var(--brand)' : 'var(--surface)',
                  color: mode === 'existing' ? '#fff' : plannedBatches.length === 0 ? 'var(--ink-faint)' : 'var(--ink)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: plannedBatches.length === 0 ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {'Existing (' + plannedBatches.length + ')'}
              </button>
              <button
                type="button"
                onClick={() => setMode('new')}
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  borderRadius: 10,
                  border: '1px solid ' + (mode === 'new' ? 'var(--brand)' : 'var(--border-strong)'),
                  background: mode === 'new' ? 'var(--brand)' : 'var(--surface)',
                  color: mode === 'new' ? '#fff' : 'var(--ink)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                + New batch
              </button>
            </div>

            {mode === 'existing' && (
              <select value={batchId} onChange={(e) => setBatchId(e.target.value)} style={inpS}>
                <option value="">Select planned batch{'…'}</option>
                {plannedBatches.map((b) => {
                  const n = Array.isArray(b.sheep_detail) ? b.sheep_detail.length : 0;
                  const when = b.planned_process_date ? ' · booked ' + b.planned_process_date : '';
                  return (
                    <option key={b.id} value={b.id}>
                      {b.name + when + ' · ' + n + (n === 1 ? ' sheep' : ' sheep')}
                    </option>
                  );
                })}
              </select>
            )}

            {/* An existing destination with no processor date. The operator
                confirms one here rather than the send assuming shipping day:
                the field is required, and the session date is only a
                suggestion they can change. */}
            {destinationNeedsDate && (
              <div style={{marginTop: 8}}>
                <label style={lblS}>Processor date for this batch *</label>
                <input
                  type="date"
                  value={newPlannedDate}
                  onChange={(e) => setNewPlannedDate(e.target.value)}
                  data-sheep-send-destination-date
                  style={inpS}
                />
                <div
                  style={{
                    fontSize: 10,
                    color: enteredDateValid ? 'var(--ink-muted)' : 'var(--danger-ink)',
                    marginTop: 3,
                  }}
                >
                  {enteredDateValid
                    ? (selectedBatch?.name || 'This batch') +
                      ' has no processor date. Suggested: ' +
                      sessionProcessingDate +
                      " (this session's date)."
                    : 'Required. Without a processor date this batch never appears on the Processing calendar.'}
                </div>
              </div>
            )}

            {mode === 'new' && (
              <div style={{display: 'flex', gap: 8}}>
                <div style={{flex: 1}}>
                  <label style={lblS}>Batch name *</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="S-26-01"
                    data-sheep-send-new-name
                    style={inpS}
                  />
                  {nameError && (
                    <div style={{fontSize: 10, color: 'var(--danger-ink)', marginTop: 3}}>
                      {nameError + ' The server will name the batch on send.'}
                    </div>
                  )}
                </div>
                <div style={{width: 140}}>
                  <label style={lblS}>Processor date *</label>
                  <input
                    type="date"
                    value={newPlannedDate}
                    onChange={(e) => setNewPlannedDate(e.target.value)}
                    data-sheep-send-new-date
                    style={inpS}
                  />
                  {!enteredDateValid && (
                    <div style={{fontSize: 10, color: 'var(--danger-ink)', marginTop: 3}}>Required.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {err && (
          <div
            style={{
              color: 'var(--danger-ink)',
              fontSize: 12,
              marginBottom: 10,
              padding: '6px 10px',
              background: 'var(--danger-soft)',
              borderRadius: 10,
            }}
          >
            {err}
          </div>
        )}

        <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
          <Button
            variant="secondary"
            // With an outcome showing, the send has ALREADY committed — this
            // button acknowledges it and lets the session finalize. Routing it
            // to onCancel would leave a committed send with an unfinished
            // session behind it.
            onClick={
              outcome
                ? () => onConfirmed({batch: outcome.batch, attached: outcome.attached, skipped: outcome.skipped})
                : onCancel
            }
            disabled={busy}
            data-sheep-send-cancel
          >
            {outcome ? 'Continue' : canSend ? 'Cancel' : 'Leave session open'}
          </Button>
          {canSend && !outcome && (
            <Button variant="primary" onClick={go} disabled={confirmDisabled}>
              {busy ? 'Attaching…' : 'Send to processor'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
